import { createContext, forwardRef, ReactNode, type PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import type { IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, Text, TextProps } from '@frontend/design-system';

interface HeaderContextValue {
  size?: 'sm' | 'md' | 'lg';
}

const HeaderContext = createContext<HeaderContextValue>({});

const headerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${theme.spacing(2)};
  > :last-child {
    margin-bottom: 0;
  }
`;

interface HeaderProps extends PropsWithChildren<React.HTMLAttributes<HTMLElement>> {
  size?: 'sm' | 'md' | 'lg';
  innerRef?: React.Ref<HTMLElement>;
  hasBottomBorder?: boolean;
}

export function Root({ children, className, size = 'md', innerRef, hasBottomBorder = false, ...rest }: HeaderProps) {
  return (
    <HeaderContext.Provider value={{ size }}>
      <header
        ref={innerRef}
        css={headerStyles}
        style={{ borderBottom: hasBottomBorder ? `1px solid ${theme.colors.neutral20}` : 'none' }}
        className={className}
        {...rest}
      >
        {children}
      </header>
    </HeaderContext.Provider>
  );
}

const sectionTitleStyles = css`
  margin-bottom: -${theme.spacing(0.75)};
  @container (min-width: ${breakpoints.small.min}px) {
    display: none;
  }
`;

type SlotProps = PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>;

export function SectionTitle({ children, ...rest }: TextProps) {
  return (
    <Text css={sectionTitleStyles} {...rest} size='large'>
      {children}
    </Text>
  );
}

const titleRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing(1)};
  min-height: 40px;
  margin-bottom: ${theme.spacing(0.75)};

  @container (min-width: ${breakpoints.large.min}px) {
    min-height: 48px;
    gap: ${theme.spacing(2)};
  }
`;

export function TitleRow({ children, ...rest }: SlotProps) {
  return (
    <div css={titleRowStyles} {...rest}>
      {children}
    </div>
  );
}

const titleContainerStyles = css`
  display: flex;
  gap: ${theme.spacing(1)};
  flex: 1;
  padding-top: 7px;
  padding-bottom: 7px;
  min-width: 0;
`;

interface TitleProps {
  children: ReactNode;
}

export function Title({ children, ...rest }: SlotProps) {
  return (
    <div css={titleContainerStyles} {...rest}>
      {children}
    </div>
  );
}

const actionsContainerStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-shrink: 0;
`;

export function Actions({ children, ...rest }: SlotProps) {
  return (
    <div css={actionsContainerStyles} {...rest}>
      {children}
    </div>
  );
}

interface SecondaryActionProps {
  children: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  className?: string;
}

export const SecondaryAction = forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      css={css`
        border: 1px solid ${theme.colors.neutral70};
        width: 40px;
        height: 40px;
        padding: 0;
      `}
      size='large'
      variant='secondary'
      {...rest}
    >
      {children}
    </Button>
  );
});

SecondaryAction.displayName = 'SecondaryAction';

interface PrimaryActionProps {
  children?: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  className?: string;
}

export const PrimaryAction = forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...rest }, ref) => {
  return (
    <Button ref={ref} size='large' {...rest}>
      {children}
    </Button>
  );
});

PrimaryAction.displayName = 'PrimaryAction';

const controlsRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1.25)};

  @container (min-width: ${breakpoints.large.min}px) {
    gap: ${theme.spacing(1)};
  }
`;

export function Controls({ children, ...rest }: SlotProps) {
  return (
    <div css={controlsRowStyles} {...rest}>
      {children}
    </div>
  );
}

export const ControlButton = forwardRef<HTMLButtonElement, ButtonProps>(({ ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant='secondary'
      size='medium'
      css={css`
        width: 36px;
        height: 36px;
        padding: 0;
      `}
      {...rest}
    />
  );
});

ControlButton.displayName = 'ControlButton';

const tabsContainerStyles = css`
  margin-left: -${theme.spacing(2)};
  margin-right: -${theme.spacing(2)};
`;

export function Tabs({ children, ...rest }: SlotProps) {
  return (
    <div css={tabsContainerStyles} {...rest}>
      {children}
    </div>
  );
}

const filtersRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
  flex-wrap: wrap;

  @container (min-width: ${breakpoints.large.min}px) {
    gap: ${theme.spacing(3)};
  }
`;

export function Filters({ children, ...rest }: SlotProps) {
  return (
    <div css={filtersRowStyles} {...rest}>
      {children}
    </div>
  );
}

const quickFiltersStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
`;

export function QuickFilters({ children, ...rest }: SlotProps) {
  return (
    <div css={quickFiltersStyles} {...rest}>
      {children}
    </div>
  );
}

const inlineRowStyles = css`
  display: flex;
  flex-direction: column-reverse;
  gap: ${theme.spacing(1.5)};
  margin-bottom: ${theme.spacing(2)};

  @container (min-width: ${breakpoints.small.min}px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export function InlineRow({ children, ...rest }: SlotProps) {
  return (
    <div css={inlineRowStyles} {...rest}>
      {children}
    </div>
  );
}

const inlineControlsStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

export function InlineControls({ children, ...rest }: SlotProps) {
  return (
    <div css={inlineControlsStyles} {...rest}>
      {children}
    </div>
  );
}

export type { HeaderProps, SlotProps, TitleProps, PrimaryActionProps, SecondaryActionProps };
